import AddIcon from '@mui/icons-material/Add'
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { PaymentRowType, Scenario, ScheduleType } from '../../shared/types'
import {
  convertScenarios,
  getRowPayoutValue,
  scenariosHaveRoundingError,
  willExceed100AfterConversion,
} from './conversion-utils'
import PayoutMethodInfobox from './payout-method-infobox'
import ScenarioCard from './scenario-card'

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
interface ScenariosSectionProps {
  scenarios: Scenario[]
  scheduleType: ScheduleType
  depositAmount?: number
  onScheduleTypeChange: (scheduleType: ScheduleType) => void
  onChange: (scenarios: Scenario[]) => void
  error?: string
  validationErrors?: Record<string, string>
  enablePartialMaturities?: boolean
  enablePartialMaturitiesByDollarAmount?: boolean
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
const EMPTY_VALIDATION_ERRORS: Record<string, string> = {}

export default function ScenariosSection({
  scenarios,
  scheduleType,
  depositAmount,
  onScheduleTypeChange,
  onChange,
  error,
  validationErrors = EMPTY_VALIDATION_ERRORS,
  enablePartialMaturities = false,
  enablePartialMaturitiesByDollarAmount = false,
}: ScenariosSectionProps) {
  const theme = useTheme()
  const [hoverAddScenario, setHoverAddScenario] = useState(false)
  const [showRoundingWarning, setShowRoundingWarning] = useState(false)
  const [showExceeds100Warning, setShowExceeds100Warning] = useState(false)

  const hasDollarAmount =
    enablePartialMaturitiesByDollarAmount &&
    scenarios.some((scenario) =>
      scenario.paymentSchedule.some(
        (row) =>
          row.inputType === PaymentRowType.DOLLAR &&
          row.payoutAmount != null &&
          row.payoutAmount > 0
      )
    )

  /**
   * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
   *
   * Check for rounding errors whenever scenarios or schedule type changes.
   * This ensures the warning shows immediately when editing values, not just
   * when switching schedule types.
   */
  useEffect(() => {
    if (!enablePartialMaturities || scenarios.length === 0) {
      setShowRoundingWarning(false)
      return
    }

    // Check if all scenarios have all fields filled
    // Treat 0 as empty since empty fields are initialized to 0
    const allFieldsFilled = scenarios.every((scenario) =>
      scenario.paymentSchedule.every((row) => {
        const amount = getRowPayoutValue(
          row,
          enablePartialMaturitiesByDollarAmount
        )
        return amount > 0 && row.paymentDate != null
      })
    )
    if (!allFieldsFilled) {
      setShowRoundingWarning(false)
      return
    }

    // In Deposit mode, only show warning if sum = 100%
    if (scheduleType === ScheduleType.DEPOSIT) {
      const allSumTo100 = scenarios.every((scenario) => {
        const total = scenario.paymentSchedule.reduce(
          (sum: number, row) =>
            sum + getRowPayoutValue(row, enablePartialMaturitiesByDollarAmount),
          0
        )
        return Math.abs(total - 100) < 0.001
      })
      if (!allSumTo100) {
        setShowRoundingWarning(false)
        return
      }
    }

    // Check if current scenarios have validation errors
    const hasValidationErrors = Object.keys(validationErrors).length > 0
    if (hasValidationErrors) {
      setShowRoundingWarning(false)
      return
    }

    // Check for rounding errors in round-trip conversion
    const hasRoundingErrors = scenariosHaveRoundingError(
      scenarios,
      scheduleType
    )
    setShowRoundingWarning(hasRoundingErrors)
  }, [
    scenarios,
    scheduleType,
    enablePartialMaturities,
    enablePartialMaturitiesByDollarAmount,
    validationErrors,
  ])

  /**
   * Check if converting from Book Value to Deposit will exceed 100%
   */
  useEffect(() => {
    if (!enablePartialMaturities || scenarios.length === 0) {
      setShowExceeds100Warning(false)
      return
    }

    // Only check when in Book Value mode
    if (scheduleType === ScheduleType.BOOK_VALUE) {
      const willExceed = willExceed100AfterConversion(scenarios, depositAmount)
      setShowExceeds100Warning(willExceed)
    } else {
      setShowExceeds100Warning(false)
    }
  }, [scenarios, scheduleType, depositAmount, enablePartialMaturities])

  /**
   * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
   *
   * Handles schedule type changes with automatic conversion of all scenario values.
   * Converts payment percentages between Deposit and Book Value formats, preserving
   * decimal precision. Shows warning if BV→D conversion has rounding errors.
   */
  const handleScheduleTypeChange = (newScheduleType: ScheduleType) => {
    // If no scenarios or type hasn't changed, just update
    if (scenarios.length === 0 || newScheduleType === scheduleType) {
      onScheduleTypeChange(newScheduleType)
      return
    }

    // Convert scenarios
    const convertedScenarios = convertScenarios(
      scenarios,
      newScheduleType,
      enablePartialMaturitiesByDollarAmount
    )

    // Update state (useEffect will handle rounding warning check)
    onChange(convertedScenarios)
    onScheduleTypeChange(newScheduleType)
  }

  const handleAddScenario = () => {
    const newScenario: Scenario = {
      id: uuidv4(),
      proposedRate: null,
      wamYears: null,
      paymentSchedule: [
        {
          payoutAmount: 0,
          payoutPercentage: 0,
          paymentDate: null,
          inputType: PaymentRowType.PERCENTAGE,
        },
      ],
      bids: [],
      selectedBids: [],
    }

    onChange([...scenarios, newScenario])
  }

  const handleDeleteScenario = (id: string) => {
    onChange(scenarios.filter((scenario) => scenario.id !== id))
  }

  const handleScenarioChange = (id: string, updatedScenario: Scenario) => {
    onChange(
      scenarios.map((scenario) =>
        scenario.id === id ? updatedScenario : scenario
      )
    )
  }

  return (
    <>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          paddingBottom: '0.5rem',
          color: theme.palette.primary.main,
          fontWeight: 500,
        }}
      >
        Scenarios
      </Typography>

      {/* Written by Windsurf using model: Claude Sonnet 4.5 (Thinking) */}
      <PayoutMethodInfobox
        scheduleType={scheduleType}
        onScheduleTypeChange={handleScheduleTypeChange}
        visible={enablePartialMaturities}
        showRoundingWarning={showRoundingWarning}
        showExceeds100Warning={showExceeds100Warning}
        hasDollarAmount={hasDollarAmount}
      />

      {((error != null && error !== '') ||
        Object.keys(validationErrors).length > 0) && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error != null && error !== ''
            ? error
            : 'Please review the scenarios below'}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.5rem',
          position: 'relative',
          mb: 2,
          alignItems: 'stretch',
        }}
      >
        {scenarios.map((scenario, index) => (
          <Box
            key={scenario.id}
            sx={{
              width: enablePartialMaturitiesByDollarAmount ? '550px' : '450px',
              minHeight: '365px',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ScenarioCard
              scenario={scenario}
              index={index}
              scheduleType={scheduleType}
              depositAmount={depositAmount}
              onDelete={handleDeleteScenario}
              onChange={handleScenarioChange}
              validationError={validationErrors[scenario.id]}
              enablePartialMaturitiesByDollarAmount={
                enablePartialMaturitiesByDollarAmount
              }
            />
          </Box>
        ))}

        <Box
          sx={{
            width: '380px',
            minHeight: '365px',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
          onMouseEnter={() => setHoverAddScenario(true)}
          onMouseLeave={() => setHoverAddScenario(false)}
        >
          <Card
            sx={{
              border: '2px dashed',
              borderColor: theme.palette.primary.light,
              backgroundColor: theme.palette.primary.light + '20',
              cursor: 'pointer',
              height: '100%',
              width: '100%',
              borderRadius: '8px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease-in-out',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                borderColor: theme.palette.primary.light,
                backgroundColor: theme.palette.primary.light + '40',
                boxShadow: '0 6px 10px rgba(0,0,0,0.08)',
              },
              position: 'relative',
            }}
            onClick={handleAddScenario}
          >
            <CardContent
              sx={{
                width: '100%',
                p: 2,
                transition: 'opacity 0.3s ease-in-out',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                }}
              >
                <Skeleton
                  variant="text"
                  width={100}
                  height={28}
                  sx={{
                    bgcolor: theme.palette.primary.light + '20',
                    transition: 'all 0.3s ease-in-out',
                  }}
                />
                <Skeleton
                  variant="circular"
                  width={20}
                  height={20}
                  sx={{
                    bgcolor: theme.palette.primary.light + '20',
                    transition: 'all 0.3s ease-in-out',
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                <Skeleton
                  variant="rounded"
                  width="45%"
                  height={36}
                  sx={{
                    bgcolor: theme.palette.primary.lightest,
                    transition: 'all 0.3s ease-in-out',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width="45%"
                  height={36}
                  sx={{
                    bgcolor: theme.palette.primary.lightest,
                    transition: 'all 0.3s ease-in-out',
                  }}
                />
              </Box>

              <Skeleton
                variant="text"
                width={120}
                height={20}
                sx={{
                  mb: 1,
                  bgcolor: theme.palette.primary.light + '20',
                  transition: 'all 0.3s ease-in-out',
                }}
              />

              <Skeleton
                variant="rounded"
                height={110}
                sx={{
                  mb: 1.5,
                  bgcolor: theme.palette.primary.light + '20',
                  transition: 'all 0.3s ease-in-out',
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Skeleton
                  variant="rounded"
                  width={80}
                  height={24}
                  sx={{
                    bgcolor: theme.palette.primary.light + '20',
                    transition: 'all 0.3s ease-in-out',
                  }}
                />
              </Box>
            </CardContent>

            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 1,
                opacity: hoverAddScenario ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out',
                pointerEvents: hoverAddScenario ? 'none' : 'auto',
              }}
            >
              <AddIcon
                fontSize="large"
                sx={{
                  mb: 1,
                  transition: 'all 0.3s ease-in-out',
                  color: theme.palette.primary.light,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  transition: 'all 0.3s ease-in-out',
                  color: theme.palette.primary.dark,
                }}
              >
                Add Scenario
              </Typography>
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  )
}
